import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { errMsg } from "@/lib/api";

// Deleting an asset in use breaks the image in the editor, the preview and the compiled sites that load it: say where before deleting.
export default function DeleteAssetDialog({ api, asset, onClose, onDeleted, prepare }) {
  const [u, setU] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setU(null); setErr("");
    if (asset) Promise.resolve(prepare?.()).catch(() => {}).then(() => api.get(`/assets/${asset.id}/usage`)).then(({ data }) => setU(data)).catch((e) => setErr(errMsg(e)));
  }, [api, asset]); // eslint-disable-line react-hooks/exhaustive-deps
  const used = !!u && !!(u.where.length || u.deploys || u.demo || u.seo);
  const del = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.delete(`/assets/${asset.id}`); onDeleted(asset); onClose(); } catch (x) { setErr(errMsg(x)); }
    setBusy(false);
  };
  return (
    <AlertDialog open={!!asset} onOpenChange={(o) => !o && !busy && onClose()}>
      <AlertDialogContent data-testid="asset-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminare «{asset?.name}»?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-slate-600">
              {!u && !err && <p className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Controllo dove è usata…</p>}
              {u && !used && <p data-testid="asset-delete-unused">Non risulta usata nel sito. Puoi eliminarla senza conseguenze.</p>}
              {u && used && (
                <div data-testid="asset-delete-used">
                  <p className="font-medium text-amber-800">{u.where.length ? `Questa immagine è usata in ${u.where.length === 1 ? "1 sezione" : `${u.where.length} sezioni`}: eliminandola sarà rimossa anche dal sito.` : "Questa immagine è usata dal sito pubblicato o dal link demo."}</p>
                  {u.where.length > 0 && <ul className="mt-1 list-disc pl-5 text-xs" data-testid="asset-delete-where">{u.where.slice(0, 8).map((w) => <li key={w}>{w}</li>)}</ul>}
                  <p className="mt-1 text-xs">Al suo posto comparirà un segnaposto grigio {[ "nell'editor", "nell'anteprima", u.deploys && "nelle compilazioni del sito che la usano (anche online)", u.demo && "nel link demo"].filter(Boolean).join(", ").replace(/, ([^,]*)$/, " e $1")}{u.seo ? "; era anche l'immagine per i social" : ""}. Per cambiarla senza rompere nulla usa «Sostituisci».</p>
                </div>)}
              {err && <p className="text-red-600" data-testid="asset-delete-error">{err}</p>}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="asset-delete-cancel">Annulla</AlertDialogCancel>
          <AlertDialogAction onClick={del} disabled={busy || (!u && !err)} className="bg-red-600 hover:bg-red-700" data-testid="asset-delete-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{used ? "Elimina comunque" : "Elimina"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>);
}
